import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Iskelet,
  Kart,
  MetinAlani,
  NotKutusu,
  Rozet,
  Secim,
  type RozetTonu,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  KORUMALI_NOT_GEREKCE_EN_AZ,
  ogrenciNotlari,
  ogrenciNotuEkle,
  ogrenciNotuKorumaliOku,
  type OgrenciNotu,
} from "../api";
import { useSudoKapisi } from "../SudoKapisi";
import type { SekmeOzellikleri } from "./sekmeler";

/**
 * NOTLAR SEKMESİ — F-PPL-04 / F-SES-04 / F-SES-05
 *
 * ================================================================
 * KORUMALI NOT AYNI KAPIDAN GEÇİYOR — SEKME BİR KESTİRME DEĞİL
 * ================================================================
 * Bu sekme en sona kaldı ve sebebi buradaydı: `StudentNote` görünürlük
 * seviyesi taşıyor ve seviyelerden biri `PROTECTED` — "sadece yazan +
 * gerekçeli yönetici erişimi".
 *
 * Sekmeyi "izne göre süzülen bir liste" olarak yazmak, korumalı notun
 * kendi kapısının (şifre + gerekçe + `ProtectedAccessLog`) ETRAFINDAN
 * dolaşan ikinci bir kapı açardı.
 *
 * Burada öyle olmuyor: liste ucu korumalı notun gövdesini **hiç
 * okumuyor** (`null` değil, anahtar yok). Gövde ayrı bir uçtan, gerekçe
 * yazıldıktan sonra geliyor ve o uç `gorusme.not_korumali.oku` iznini
 * kullandığı için **şifre sorusu kendiliğinden** devreye giriyor
 * (`auth/sudo.ts`). Ekran sudo'yu kendi listesinden bilmiyor: sunucu
 * `SUDO_GEREKLI` dediğinde `useSudoKapisi` soruyor.
 *
 * ================================================================
 * DÜZENLEME VE GÖRÜNÜRLÜK DEĞİŞTİRME YOK — BİLEREK
 * ================================================================
 * F-SES-04 görünürlüğün yalnızca **daraltılabileceğini** söylüyor
 * (`visibilityLoweredAt` bu yüzden var). O kuralın yarısını yazmak —
 * mesela "seviyeyi değiştir" düğmesi koyup daraltma kontrolünü
 * atlamak — hiç yazmamaktan kötü olurdu: kullanıcıya geri alınamaz bir
 * işlemi geri alınabilir gibi gösterirdi.
 *
 * Bu turda not YAZILIYOR ve OKUNUYOR; düzenleme ve seviye değişikliği
 * kendi turunda, `visibilityLoweredAt` kuralıyla birlikte gelecek.
 *
 * ================================================================
 * `PersonalNote` BURAYA HİÇ GİRMİYOR
 * ================================================================
 * Depoda üç ayrı "not" var; karışması en kolay olanı bu. `PersonalNote`
 * kişinin KENDİNE yazdığı not ve öğrenciye bağlı bile değil.
 */

const GORUNURLUK_ADI: Record<string, string> = {
  INTERNAL: "Personel",
  STUDENT: "Öğrenci görebilir",
  PARENT: "Veli görebilir",
  SHARED: "Herkes",
  PROTECTED: "Korumalı",
  PRIVATE: "Yalnızca yazan",
};

const GORUNURLUK_TONU: Record<string, RozetTonu> = {
  INTERNAL: "notr",
  STUDENT: "bilgi",
  PARENT: "bilgi",
  SHARED: "onay",
  PROTECTED: "uyari",
  PRIVATE: "notr",
};

/**
 * Yazarken seçilebilecek seviyeler.
 *
 * `PRIVATE` YOK: sunucu da reddediyor. Öğrenciye bağlı ama kimsenin
 * göremeyeceği bir kayıt, devirden sonra sonsuza kadar kilitli kalırdı
 * — eski uzman öğrenciye erişemez, yeni uzman notu göremez.
 */
const YAZILABILIR = ["INTERNAL", "STUDENT", "PARENT", "SHARED", "PROTECTED"] as const;

/**
 * Gerekçe eşiği — SUNUCUDAKİ SABİTTEN.
 *
 * `15` yazmıştım. Aynı sayı `contracts`ta, görüşme servisinde, öğrenci
 * notu servisinde ve burada tutmak zorunda; elle yazılan her kopya o
 * tutmayı bir TESADÜFE çeviriyor — ve ayrıştığı gün ekran düğmeyi
 * açar, sunucu reddeder.
 */
const GEREKCE_EN_AZ = KORUMALI_NOT_GEREKCE_EN_AZ;

function gun(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR");
}

export function Notlar({ ogrenciId, izinVar }: SekmeOzellikleri) {
  const qc = useQueryClient();
  const [yaziyor, setYaziyor] = useState(false);

  const liste = useQuery({
    queryKey: ["ogrenci-notlari", ogrenciId],
    queryFn: () => ogrenciNotlari(ogrenciId),
    retry: false,
  });

  return (
    <div className="flex flex-col gap-4">
      {izinVar("kisi.ogrenci.duzenle") &&
        (yaziyor ? (
          <NotFormu
            ogrenciId={ogrenciId}
            korumaliYazabilir={izinVar("gorusme.not_korumali.yaz")}
            onKapat={() => setYaziyor(false)}
          />
        ) : (
          <div>
            <Buton onClick={() => setYaziyor(true)}>Not ekle</Buton>
          </div>
        ))}

      {liste.isPending ? (
        <Iskelet satir={4} />
      ) : liste.isError ? (
        <p className="text-hata-700 text-sm">Notlar alınamadı.</p>
      ) : liste.data.length === 0 ? (
        <p className="text-notr-600 text-sm">Bu öğrenci için not yazılmamış.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {liste.data.map((n) => (
            <li key={n.id}>
              <NotKarti
                not={n}
                ogrenciId={ogrenciId}
                okuyabilir={izinVar("gorusme.not_korumali.oku")}
                onOkundu={() =>
                  void qc.invalidateQueries({ queryKey: ["ogrenci-notlari", ogrenciId] })
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NotKarti({
  not,
  ogrenciId,
  okuyabilir,
}: {
  not: OgrenciNotu;
  ogrenciId: string;
  okuyabilir: boolean;
  onOkundu: () => void;
}) {
  const [gerekce, setGerekce] = useState("");
  const [acik, setAcik] = useState(false);
  const { hataYakala, kapi } = useSudoKapisi(
    "Korumalı bir notu okumak üzeresiniz. Erişim kayda geçer.",
  );

  const oku = useMutation({
    mutationFn: () => ogrenciNotuKorumaliOku(ogrenciId, not.id, gerekce.trim()),
    /*
      SUDO KAPISI: sunucu `SUDO_GEREKLI` derse pencere aciliyor ve
      dogrulama bitince islem KALDIGI YERDEN devam ediyor. Ekran hangi
      islemin sudo istedigini KENDI bilmiyor — bilseydi `sudo.ts` ile
      ayristigi gun ya gereksiz sifre sorar ya sormadan gonderirdi.
    */
    onError: hataYakala(() => oku.mutate()),
  });

  return (
    <Kart
      baslik={not.baslik ?? (not.korumali ? "Korumalı not" : "Not")}
      aciklama={`${not.yazan} · ${gun(not.olusturma)}`}
      eylem={
        <div className="flex items-center gap-2">
          {not.sabit && <Rozet ton="bilgi">Sabit</Rozet>}
          <Rozet ton={GORUNURLUK_TONU[not.gorunurluk] ?? "notr"}>
            {GORUNURLUK_ADI[not.gorunurluk] ?? not.gorunurluk}
          </Rozet>
        </div>
      }
    >
      {kapi}

      {/*
        ================================================================
        ÜÇ HÂL, ÜÇ AYRI CÜMLE
        ================================================================
        `govde` anahtarı VARSA gövde gösteriliyor.
        Anahtar YOKSA ve not korumalıysa → kapı.
        Anahtar YOKSA ve not korumalı DEĞİLSE → bu okuyucu o seviyeyi
        göremiyor; kart zaten listeye girmemeliydi, ama sessiz boş bir
        kutu bırakmıyoruz.
      */}
      {not.govde !== undefined ? (
        <p className="text-notr-800 text-sm whitespace-pre-wrap">{not.govde}</p>
      ) : not.korumali ? (
        <div className="flex flex-col gap-3">
          <NotKutusu ton="uyari">
            Bu not <strong>korumalı</strong>. Gövdesini açmak şifrenizi ve yazılı bir gerekçe ister;
            erişim <strong>kayda geçer</strong> ve silinemez.
          </NotKutusu>

          {!okuyabilir ? (
            /*
              Izin yoksa form HIC cizilmiyor: doldurulup reddedilecek bir
              alan, kullaniciya yapabilecegi bir sey varmis gibi gosterir.
            */
            <p className="text-notr-600 text-sm">
              Korumalı not okuma yetkiniz yok. Gerekiyorsa notu yazan kişiye ya da yöneticinize
              başvurun.
            </p>
          ) : acik ? (
            <>
              <MetinAlani
                etiket="Erişim gerekçesi"
                yardim={`En az ${GEREKCE_EN_AZ} karakter. Bu metin erişim kaydına aynen yazılır.`}
                value={gerekce}
                onChange={(e) => setGerekce(e.target.value)}
                rows={2}
              />
              {oku.isError && (
                <p role="alert" className="text-hata-700 text-sm">
                  {oku.error instanceof ApiHatasi ? oku.error.message : "Not açılamadı."}
                </p>
              )}
              {oku.data ? (
                <div className="rounded-alan border-uyari-200 bg-uyari-50 flex flex-col gap-1 border p-3">
                  <p className="text-notr-800 text-sm whitespace-pre-wrap">{oku.data.govde}</p>
                  <p className="text-notr-600 text-xs">Yazan: {oku.data.yazan}</p>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Buton
                    yukleniyor={oku.isPending}
                    disabled={gerekce.trim().length < GEREKCE_EN_AZ}
                    onClick={() => oku.mutate()}
                  >
                    Notu aç
                  </Buton>
                  <Buton gorunum="ikincil" onClick={() => setAcik(false)}>
                    Vazgeç
                  </Buton>
                </div>
              )}
            </>
          ) : (
            <div>
              <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
                Gerekçeyle aç
              </Buton>
            </div>
          )}
        </div>
      ) : (
        <p className="text-notr-600 text-sm">Bu notun içeriğini görüntüleme yetkiniz yok.</p>
      )}
    </Kart>
  );
}

function NotFormu({
  ogrenciId,
  korumaliYazabilir,
  onKapat,
}: {
  ogrenciId: string;
  korumaliYazabilir: boolean;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [baslik, setBaslik] = useState("");
  const [govde, setGovde] = useState("");
  const [gorunurluk, setGorunurluk] = useState<string>("INTERNAL");
  const [sabit, setSabit] = useState(false);

  const ekle = useMutation({
    mutationFn: () =>
      ogrenciNotuEkle(ogrenciId, {
        baslik: baslik.trim() || null,
        govde: govde.trim(),
        gorunurluk,
        sabit,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["ogrenci-notlari", ogrenciId] });
      onKapat();
    },
  });

  /*
    KORUMALI SECENEGI IZNE BAGLI. Sunucu zaten `gorusme.not_korumali.yaz`
    istiyor; secenegi gostermemek yalnizca kullaniciyi reddedilecek bir
    secimle karsilastirmamak icin.
  */
  const secenekler = YAZILABILIR.filter((g) => g !== "PROTECTED" || korumaliYazabilir);

  return (
    <Kart baslik="Yeni not" aciklama="Görünürlük seviyesi sonradan değiştirilemez" /* F-SES-04 */>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (govde.trim().length > 0) ekle.mutate();
        }}
      >
        <Alan
          etiket="Başlık"
          value={baslik}
          onChange={(e) => setBaslik(e.target.value)}
          maxLength={140}
          yardim="İsteğe bağlı."
        />

        <MetinAlani
          etiket="Not"
          value={govde}
          onChange={(e) => setGovde(e.target.value)}
          rows={5}
          maxLength={4000}
        />

        <Secim
          etiket="Kimler görebilir"
          value={gorunurluk}
          onChange={(e) => setGorunurluk(e.target.value)}
          /*
            SEVIYE SONRADAN DEGISMIYOR ve bu YAZMADAN ONCE soyleniyor.
            Sonradan soylemek, kullaniciyi geri alamayacagi bir secimden
            SONRA bilgilendirmek olurdu.
          */
          yardim="Seçim kaydedildikten sonra değiştirilemez."
        >
          {secenekler.map((g) => (
            <option key={g} value={g}>
              {GORUNURLUK_ADI[g]}
            </option>
          ))}
        </Secim>

        {gorunurluk === "PROTECTED" && (
          <NotKutusu ton="uyari">
            Korumalı not <strong>şifreli saklanır</strong>. Sizden başkası okumak istediğinde şifre
            ve yazılı gerekçe ister, erişim kayda geçer.
          </NotKutusu>
        )}

        <label className="text-notr-700 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sabit}
            onChange={(e) => setSabit(e.target.checked)}
            className="ring-notr-300 size-4 rounded ring-1"
          />
          Listenin başına sabitle
        </label>

        {ekle.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {ekle.error instanceof ApiHatasi ? ekle.error.message : "Not kaydedilemedi."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton type="submit" yukleniyor={ekle.isPending} disabled={govde.trim().length === 0}>
            Kaydet
          </Buton>
          <Buton gorunum="ikincil" type="button" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}
